import { FabButton } from 'components/uikit/buttons/FabButton';
import React, { useState } from 'react';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import styles from './ProjectListPage.module.scss';
import { ProjectCard } from 'pages/authorized/projects/components/projectCard/ProjectCard';
import { ProjectModal } from './components/projectModal/ProjectModal.tsx';
import { Loading } from 'components/uikit/suspense/Loading';
import { StringParam, useQueryParams } from 'react-router-url-params';
import { SearchInput } from 'components/uikit/inputs/SearchInput.tsx';
import { useDebounce } from 'use-debounce';
import NoProjectsSearch from 'assets/icons/no-projects-search.svg?react';
import { NextPageTrigger } from 'helpers/infiniteScrolling/NextPageTrigger.tsx';
import {
  infiniteScrollingSortingToBackendRequest,
  sortingQueryParams,
} from 'helpers/infiniteScrolling/getInfiniteQueryDefaults.ts';
import { useProjectInfiniteQuery } from './useProjectInfiniteScrolling.ts';
import { useInfiniteScrolling } from 'helpers/infiniteScrolling/useInfiniteScrolling.ts';
import { IconLabelView } from 'components/uikit/iconLabel/IconLabelView.tsx';
import { ProjectProvider } from 'helpers/projectPermissions/ProjectContext.tsx';

export const ProjectListPage: React.FC = () => {
  const i18n = useScopedTranslation('Page.Projects.List');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [queryParams] = useQueryParams({
    search: StringParam,
    ...sortingQueryParams(),
  });
  const [searchingProject, setSearchingProject] = useState('');
  const [debouncedSearchingProject] = useDebounce(searchingProject, 500);
  const projectsQuery = useProjectInfiniteQuery(
    {
      pageSize: 50,
      queryParams: {
        search: debouncedSearchingProject,
        ...infiniteScrollingSortingToBackendRequest(queryParams),
      },
    },
    {
      retry: true,
    },
  );

  const projects = useInfiniteScrolling(projectsQuery, ({ data }) => data);

  const isEmptySearchResult = projects.flatData.length == 0;

  return (
    <div className={styles.container} data-test-id="project-list-page">
      <div className={styles.topBar}>
        <div className={styles.projectName}>{i18n.t('projects')}</div>
        <div className={styles.searchContainer}>
          <SearchInput
            className={styles.searchInput}
            onChange={(e) => {
              setSearchingProject(e.currentTarget.value);
            }}
            maxLength={70}
          />
        </div>
      </div>

      <Loading
        loading={projectsQuery.isLoading}
        className={styles.loader}
        wrapperClassName={styles.loaderWrapper}
      >
        {isEmptySearchResult && !projectsQuery.isFetching ? (
          <IconLabelView
            icon={<NoProjectsSearch />}
            label={i18n.t('no_projects_search_result')}
          />
        ) : (
          <div className={styles.project_items}>
            {projects.flatData.map((project) => (
              <ProjectCard
                project={project}
                className={styles.project_item}
                key={project.id}
              />
            ))}
          </div>
        )}
        <NextPageTrigger ref={projects.ref} />
      </Loading>

      <FabButton
        title={i18n.t('add_button')}
        className={styles.addButton}
        onClick={() => setIsModalOpen(true)}
      ></FabButton>

      <ProjectModal
        onClose={() => setIsModalOpen(false)}
        isOpen={isModalOpen}
      />
    </div>
  );
};
